import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  InputAdornment,
  Stack,
  styled,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  Snackbar,
  Fade,
  Zoom,
  alpha,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  useRef,
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
} from "react";
import { Helmet } from "react-helmet-async";
import { Markdown } from "src/components/markdown";
import Scrollbar from "src/components/scrollbar";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import BatchPredictionRoundedIcon from "@mui/icons-material/BatchPredictionRounded";
import ScatterPlotRoundedIcon from "@mui/icons-material/ScatterPlotRounded";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import { NAV } from "src/layouts/dashboard/config-layout";
import axios from "axios";
import { keyframes } from "@emotion/react";
import useAuth from "src/hooks/useAuth";
import { logEvent } from "firebase/analytics";
import { analytics } from "src/config/firebase.config";
import GradientText from "src/components/GradientText";
import { BubbleChartRounded } from "@mui/icons-material";

const shimmerAnimation = keyframes`
  0% {
    background-position: -200% 0%;
  }
  100% {
    background-position: 200% 0%;
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 5px rgba(116, 79, 168, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(116, 79, 168, 0.6), 0 0 30px rgba(116, 79, 168, 0.4);
  }
`;

const GlassMorphicCard = styled(Card)(({ theme, isUser = false }) => ({
  background: isUser
    ? `linear-gradient(135deg, ${alpha(
        theme.palette.background.paper,
        0.9
      )} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`
    : "transparent",
  backdropFilter: "blur(20px)",
  borderRadius: isUser ? "24px 8px 8px 24px" : "24px",
  border: "none",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  color: theme.palette.text.primary,
  "&:hover": {
    boxShadow: isUser
      ? `0 12px 40px ${alpha(
          theme.palette.background.paper,
          theme.palette.mode === "dark" ? 0.15 : 0.08
        )}`
      : `0 12px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

const ModernInputCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.9
  )} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  borderRadius: "24px",
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:focus-within": {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
}));

const DeepAnalysisToggle = styled(Box)(({ theme, enabled }) => ({
  padding: theme.spacing(1, 2),
  borderRadius: "20px",
  border: `2px solid ${
    enabled ? theme.palette.primary.main : alpha(theme.palette.divider, 0.3)
  }`,
  background: enabled
    ? `linear-gradient(135deg, ${alpha(
        theme.palette.primary.main,
        0.15
      )} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
    : "transparent",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  minWidth: "120px",
  justifyContent: "center",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    background: enabled
      ? `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.2
        )} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`
      : `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.08
        )} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
    transform: "translateY(-1px)",
    boxShadow: enabled
      ? `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`
      : `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
  "&::before": enabled
    ? {
        content: '""',
        position: "absolute",
        top: 0,
        left: "-100%",
        width: "100%",
        height: "100%",
        background: `linear-gradient(90deg, transparent, ${alpha(
          theme.palette.primary.main,
          0.2
        )}, transparent)`,
        animation: `${shimmerAnimation} 2s infinite`,
      }
    : {},
}));

const ElapsedTimeChip = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  borderRadius: "12px",
  background: `linear-gradient(135deg, ${alpha("#497DFF", 0.15)} 0%, ${alpha(
    "#497DFF",
    0.1
  )} 100%)`,
  color: "#497DFF",
  fontSize: "11px",
  fontWeight: 600,
  border: `1px solid ${alpha("#497DFF", 0.2)}`,
  animation: `${pulseGlow} 2s ease-in-out infinite`,
}));

const StyledIconButton = styled(IconButton)(
  ({ theme, variant = "default" }) => ({
    borderRadius: "12px",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    ...(variant === "send" && {
      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
      color: "white",
      "&:hover": {
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        transform: "scale(1.05)",
      },
      "&:disabled": {
        background: alpha(theme.palette.action.disabled, 0.12),
        color: theme.palette.action.disabled,
      },
    }),
    ...(variant === "stop" && {
      background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
      color: "white",
      "&:hover": {
        background: `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 100%)`,
        transform: "scale(1.05)",
      },
    }),
  })
);

class ChatService {
  constructor() {
    this.currentController = null;
  }

  cancelCurrentRequest() {
    if (this.currentController) {
      this.currentController.abort();
      this.currentController = null;
    }
  }

  async sendStreamedMessage({
    chat,
    deepAnalysis,
    deepDive,
    addChat,
    setLoading,
    setLoadingMessage,
    setInCommingMessage,
    scrollToBottom,
    showError,
    authToken,
  }) {
    this.cancelCurrentRequest();
    this.currentController = new AbortController();

    setLoading(true);
    setInCommingMessage("");
    let inMessage = "";

    if (deepAnalysis) {
      setLoadingMessage("Analysing...");
    } else if (deepDive) {
      setLoadingMessage("Uncovering details...");
    } else {
      setLoadingMessage("Thinking about it...");
    }

    const url = "https://ai.riple.org";

    try {
      const timeoutMs = deepAnalysis || deepDive ? 600000 : 180000;

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("TIMEOUT")), timeoutMs)
      );

      const fetchPromise = fetch(url, {
        method: "POST",
        body: JSON.stringify({
          messages: chat,
          deepAnalysis: deepAnalysis || deepDive,
          deepDive,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: authToken
            ? `Bearer ${authToken}`
            : `${axios.defaults.headers.common.Authorization}`,
        },
        signal: this.currentController.signal,
      });

      const response = await Promise.race([fetchPromise, timeoutPromise]);

      if (!response.ok) {
        let errorData;
        let isServerError = true;

        try {
          errorData = await response.json();
        } catch {
          try {
            errorData = await response.text();
          } catch {
            errorData = { message: `Server error (${response.status})` };
          }
        }

        let errorMessage = "Connection failed";

        if (typeof errorData === "object" && errorData !== null) {
          errorMessage =
            errorData.message || errorData.error || "Server error occurred";

          switch (errorData.errorCode) {
            case "SAANVI-LIMIT-EXCEEDED":
              errorMessage =
                "Anonymous chat limit exceeded. Please login to continue.";
              break;
            case "SAANVI-PROCESSING-ERROR":
              errorMessage = "Failed to process request. Please try again.";
              break;
            case "SAANVI-INVALID-BODY":
              errorMessage = "Invalid request format.";
              break;
            case "SAANVI-INVALID-MESSAGES":
              errorMessage = "Invalid message format.";
              break;
            default:
              break;
          }
        } else if (typeof errorData === "string") {
          try {
            const parsed = JSON.parse(errorData);
            errorMessage = parsed.message || parsed.error || errorData;
          } catch {
            errorMessage = errorData;
          }
        }

        showError(errorMessage, isServerError);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let firstChunkReceived = false;

      while (true) {
        const { value, done } = await reader.read();

        if (done) {
          if (inMessage.trim()) {
            addChat({ role: "assistant", content: inMessage });
          }
          setInCommingMessage("");
          break;
        }

        if (!firstChunkReceived && (deepAnalysis || deepDive)) {
          setLoadingMessage("Thinking about it...");
          firstChunkReceived = true;
        }

        let chunkText = decoder.decode(value, { stream: true });

        if (chunkText.startsWith("data: ")) {
          chunkText = chunkText.substring(6);
        }

        chunkText = chunkText.replace(/\n\n$/, "");

        if (chunkText.includes("[DONE]")) {
          continue;
        }

        const splits = chunkText.split("\n\n").filter(Boolean);

        for (const part of splits) {
          let cleaned = part;

          if (cleaned.startsWith("data: ")) {
            cleaned = cleaned.substring(6);
          }

          cleaned = cleaned.replace(/\n$/, "");

          if (!cleaned || cleaned.includes("[DONE]")) {
            continue;
          }

          try {
            const parsed = JSON.parse(cleaned);
            const messageContent = parsed?.choices?.[0]?.delta?.content;

            if (messageContent && typeof messageContent === "string") {
              inMessage += messageContent;
              setInCommingMessage(inMessage);
              scrollToBottom();
            }
          } catch (e) {
            console.log("Error parsing chunk:", e);
            continue;
          }
        }
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Request cancelled by user");
        return;
      }

      let errorMessage = "An unexpected error occurred. Please try again.";
      let isServerError = false;

      if (error.message === "TIMEOUT") {
        errorMessage = deepAnalysis
          ? "Deep analysis is taking longer than expected. The server might be busy. Please try again."
          : "Response timeout. Please try again.";
      } else if (
        error.name === "TypeError" &&
        error.message.includes("fetch")
      ) {
        errorMessage = "No internet connection. Please check your network.";
      }

      showError(errorMessage, isServerError);
      console.error("Streaming error:", error);
    } finally {
      setLoading(false);
      setLoadingMessage("Thinking about it...");
      this.currentController = null;
    }
  }
}

const chatService = new ChatService();

// Create a context for modal management
const ModalContext = createContext(null);

// Modal Provider Component
export const SaanviModalProvider = ({ children }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalInitialPrompt, setModalInitialPrompt] = useState(null);
  const [modalShowInitialPrompt, setModalShowInitialPrompt] = useState(true);

  const showModal = useCallback(
    (initialPrompt = null, showInitialPrompt = true) => {
      setModalInitialPrompt(initialPrompt);
      setModalShowInitialPrompt(showInitialPrompt);
      setModalOpen(true);
    },
    []
  );

  const hideModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  return (
    <ModalContext.Provider value={{ showModal, hideModal }}>
      {children}
      <SaanviModal
        open={modalOpen}
        onClose={hideModal}
        initialPrompt={modalInitialPrompt}
        showInitialPrompt={modalShowInitialPrompt}
      />
    </ModalContext.Provider>
  );
};

// Hook to use modal
export const useSaanviModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useSaanviModal must be used within a SaanviModalProvider");
  }
  return context;
};

// Export function for backwards compatibility
export const showSaanviModal = (
  initialPrompt = null,
  showInitialPrompt = true
) => {
  console.warn(
    "showSaanviModal is deprecated. Use useSaanviModal hook instead."
  );
};

// Modal component
const SaanviModal = ({
  open,
  onClose,
  initialPrompt,
  showInitialPrompt = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: "90vh",
          borderRadius: isMobile ? 0 : 3,
        },
      }}
    >
      <DialogContent sx={{ p: 0, height: "100%" }}>
        <SaanviPage
          isModal={true}
          initialPrompt={initialPrompt}
          showInitialPrompt={showInitialPrompt}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  );
};

export default function SaanviPage({
  isModal = false,
  initialPrompt = null,
  showInitialPrompt = true,
  onClose = null,
}) {
  const { isAuthenticated } = useAuth();

  const [chat, setChat] = useState([]);
  const [message, setMessage] = useState("");
  const [inCommingMessage, setInCommingMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [deepAnalysis, setDeepAnalysis] = useState(false);
  const [deepDive, setDeepDive] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Thinking about it...");
  const [errorMessage, setErrorMessage] = useState(null);
  const [analysisStartTime, setAnalysisStartTime] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(-1);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [elapsedTime, setElapsedTime] = useState("");
  const [initialPromptProcessed, setInitialPromptProcessed] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const messagesEndRef = useRef();
  const scrollRef = useRef();

  // Handle initial prompt - Fixed dependency array and logic
  useEffect(() => {
    if (initialPrompt && !initialPromptProcessed) {
      setInitialPromptProcessed(true);

      if (showInitialPrompt) {
        setMessage(initialPrompt);
      } else {
        // Send initial prompt silently
        const sendInitialPrompt = async () => {
          setErrorMessage(null);
          if (deepAnalysis || deepDive) {
            setAnalysisStartTime(Date.now());
          }

          await chatService.sendStreamedMessage({
            chat: [{ role: "user", content: initialPrompt }],
            deepAnalysis,
            deepDive,
            addChat: (newMessage) => {
              setChat([{ role: "user", content: initialPrompt }, newMessage]);
              setAnalysisStartTime(null);
            },
            setLoading,
            setLoadingMessage,
            setInCommingMessage,
            scrollToBottom,
            showError,
            authToken: axios.defaults.headers.common.Authorization?.replace(
              "Bearer ",
              ""
            ),
          });

          scrollToBottom();
        };

        sendInitialPrompt();
      }
    }
  }, [
    initialPrompt,
    showInitialPrompt,
    deepAnalysis,
    deepDive,
    initialPromptProcessed,
  ]);

  // Elapsed time updater
  useEffect(() => {
    let interval;
    if (analysisStartTime && (deepAnalysis || deepDive)) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - analysisStartTime) / 1000);
        if (elapsed < 60) {
          setElapsedTime(`${elapsed}s`);
        } else {
          const minutes = Math.floor(elapsed / 60);
          const seconds = elapsed % 60;
          setElapsedTime(`${minutes}m ${seconds}s`);
        }
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [analysisStartTime, deepAnalysis, deepDive]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      scrollRef.current?.scrollTo(0, messagesEndRef.current?.scrollHeight);
    }, 100);
  }, []);

  const showError = useCallback((message, isServerError) => {
    setErrorMessage(message);
    setLoading(false);
    setLoadingMessage("Thinking about it...");
    setAnalysisStartTime(null);

    setSnackbar({
      open: true,
      message,
      severity: isServerError ? "error" : "warning",
      isServerError,
    });

    setTimeout(() => {
      setErrorMessage(null);
    }, 10000);
  }, []);

  const stopGeneration = useCallback(() => {
    chatService.cancelCurrentRequest();
    setLoading(false);
    setLoadingMessage("Thinking about it...");
    setErrorMessage(null);
    setAnalysisStartTime(null);
  }, []);

  const handleSendMessage = useCallback(
    async (e) => {
      e.preventDefault();

      if (loading || !message.trim()) return;

      logEvent(analytics, "button_click", { button_name: "Saanvi Chat Send" });

      const limitExceeded =
        !!localStorage.getItem("SAANVI-LIMIT-EXCEEDED") && !isAuthenticated;
      if (limitExceeded) return;

      const newChat = [...chat, { role: "user", content: message.trim() }];
      setChat(newChat);
      setMessage("");
      setErrorMessage(null);

      if (deepAnalysis || deepDive) {
        setAnalysisStartTime(Date.now());
      }

      await chatService.sendStreamedMessage({
        chat: newChat,
        deepAnalysis,
        deepDive,
        addChat: (newMessage) => {
          setChat((prev) => [...prev, newMessage]);
          setAnalysisStartTime(null);
        },
        setLoading,
        setLoadingMessage,
        setInCommingMessage,
        scrollToBottom,
        showError,
        authToken: axios.defaults.headers.common.Authorization?.replace(
          "Bearer ",
          ""
        ),
      });

      scrollToBottom();
    },
    [
      loading,
      message,
      chat,
      deepAnalysis,
      deepDive,
      isAuthenticated,
      scrollToBottom,
      showError,
    ]
  );

  const copyToClipboard = useCallback(async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setSnackbar({
        open: true,
        message: "Copied to clipboard!",
        severity: "success",
      });
      setTimeout(() => setCopiedIndex(-1), 3000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  }, []);

  const newChat = useCallback(() => {
    chatService.cancelCurrentRequest();
    setChat([]);
    setLoading(false);
    setInCommingMessage("");
    setLoadingMessage("Thinking about it...");
    setErrorMessage(null);
    setAnalysisStartTime(null);
    setInitialPromptProcessed(false);
  }, []);

  const handleDeepAnalysisToggle = useCallback(() => {
    if (deepAnalysis) {
      // If already active, turn it off
      setDeepAnalysis(false);
    } else {
      // If not active, turn it on and turn off deep dive
      setDeepAnalysis(true);
      setDeepDive(false);
    }
  }, [deepAnalysis]);

  const handleDeepDiveToggle = useCallback(() => {
    if (deepDive) {
      // If already active, turn it off
      setDeepDive(false);
    } else {
      // If not active, turn it on and turn off deep analysis
      setDeepDive(true);
      setDeepAnalysis(false);
    }
  }, [deepDive]);

  const handleSnackbarClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <>
      <Helmet>
        <title> Riple - Saanvi Chat </title>
      </Helmet>

      <Box
        sx={{
          m: "-16px",
          mt: 0,
          minHeight: "100vh",
          background: `linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.02)} 0%, 
            ${alpha(theme.palette.background.default, 1)} 25%,
            ${alpha(theme.palette.background.default, 1)} 75%,
            ${alpha(theme.palette.secondary.main, 0.02)} 100%
          )`,
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            height: isModal
              ? "100vh"
              : `calc(100vh - ${isMobile ? NAV.MOBILE : 0}px)`,
            display: "flex",
            flexDirection: "column",
            py: isMobile ? 1 : 2,
          }}
        >
          {/* Header */}
          <AppBar
            position="static"
            sx={{ background: "transparent", border: "none" }}
            elevation={0}
          >
            <Toolbar>
              <Stack
                direction={isMobile ? "column" : "row"}
                alignItems={isMobile ? "start" : "center"}
                justifyContent={isMobile ? "center" : "start"}
                gap={2}
                height="70px"
                flexGrow={1}
              >
                <Box
                  component="img"
                  src="/assets/saanvi_logo.png"
                  height="20px"
                  width="200px"
                  sx={{ objectFit: "contain" }}
                />
              </Stack>

              <Stack direction="row" spacing={1}>
                {isModal && (
                  <IconButton onClick={onClose}>
                    <CloseIcon />
                  </IconButton>
                )}
                <Button
                  onClick={newChat}
                  startIcon={<RestartAltRoundedIcon />}
                  color="secondary"
                  variant="outlined"
                  size="small"
                >
                  New Chat
                </Button>
              </Stack>
            </Toolbar>
          </AppBar>

          {/* Welcome Screen */}
          {!chat.length && (
            <Fade in timeout={800}>
              <Stack
                sx={{ flexGrow: 1 }}
                alignItems="center"
                justifyContent="center"
                gap={4}
                textAlign="center"
              >
                <Box>
                  <GradientText variant={isMobile ? "h3" : "h2"} sx={{ mb: 2 }}>
                    Hello, I'm Saanvi
                  </GradientText>

                  <Typography
                    variant={isMobile ? "h6" : "h5"}
                    color="text.secondary"
                    sx={{
                      fontWeight: 400,
                      maxWidth: 600,
                      mx: "auto",
                      lineHeight: 1.4,
                    }}
                  >
                    What would you like to know today?
                  </Typography>
                </Box>

                {(deepAnalysis || deepDive) && (
                  <Zoom in timeout={250}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: "24px",
                        background: `linear-gradient(135deg, ${alpha(
                          theme.palette.primary.main,
                          0.1
                        )} 0%, ${alpha(
                          theme.palette.primary.main,
                          0.05
                        )} 100%)`,
                        border: `1px solid ${alpha(
                          theme.palette.primary.main,
                          0.2
                        )}`,
                        backdropFilter: "blur(20px)",
                        textAlign: "center",
                        maxWidth: 400,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                        mb={1}
                      >
                        <AutoAwesomeIcon
                          sx={{ fontSize: 20, color: "primary.main" }}
                        />
                        <Typography
                          variant="subtitle1"
                          color="primary.main"
                          fontWeight={600}
                        >
                          Deep {deepDive ? "Dive" : "Analysis"} Mode
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        color="primary.main"
                        sx={{ opacity: 0.8 }}
                      >
                        Enhanced reasoning and thorough analysis enabled
                      </Typography>
                    </Box>
                  </Zoom>
                )}
              </Stack>
            </Fade>
          )}

          {/* Chat Messages */}
          {chat.length > 0 && (
            <Scrollbar ref={scrollRef} sx={{ flexGrow: 1, mb: 2 }}>
              <Stack ref={messagesEndRef} gap={3} sx={{ py: 2, px: 1 }}>
                {chat?.map((messageObj, idx) => (
                  <Fade key={idx} in timeout={400}>
                    <Stack
                      direction="column"
                      alignItems={
                        messageObj.role === "user" ? "flex-end" : "flex-start"
                      }
                    >
                      <GlassMorphicCard
                        isUser={messageObj.role === "user"}
                        sx={{
                          maxWidth: messageObj.role === "user" ? "85%" : "100%",
                          width: messageObj.role === "user" ? "auto" : "100%",
                        }}
                      >
                        <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                          <Markdown>{messageObj.content}</Markdown>

                          <Stack
                            direction="row"
                            justifyContent={
                              messageObj.role === "user"
                                ? "flex-end"
                                : "flex-start"
                            }
                            mt={2}
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                copyToClipboard(messageObj.content, idx)
                              }
                              disabled={copiedIndex === idx}
                              sx={{
                                bgcolor: alpha(theme.palette.action.hover, 0.5),
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.action.hover,
                                    0.8
                                  ),
                                },
                              }}
                            >
                              {copiedIndex === idx ? (
                                <CheckRoundedIcon
                                  sx={{ fontSize: 16, color: "success.main" }}
                                />
                              ) : (
                                <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
                              )}
                            </IconButton>
                          </Stack>
                        </CardContent>
                      </GlassMorphicCard>
                    </Stack>
                  </Fade>
                ))}

                {/* Loading State */}
                {loading && (
                  <Fade in timeout={300}>
                    <Stack alignItems="flex-start">
                      <GlassMorphicCard sx={{ width: "100%" }}>
                        <CardContent sx={{ p: 3 }}>
                          <Collapse
                            in={loading && !inCommingMessage}
                            orientation="vertical"
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={1.5}
                              mb={2}
                            >
                              {loadingMessage === "Analysing..." && (
                                <ScatterPlotRoundedIcon
                                  sx={{
                                    fontSize: 18,
                                    color: "primary.main",
                                  }}
                                />
                              )}
                              {loadingMessage === "Uncovering details..." && (
                                <BubbleChartRounded
                                  sx={{
                                    fontSize: 18,
                                    color: "primary.main",
                                  }}
                                />
                              )}
                              <GradientText
                                variant="body1"
                                sx={{ fontWeight: 500 }}
                              >
                                {loadingMessage}
                              </GradientText>
                              {analysisStartTime &&
                                (deepAnalysis || deepDive) && (
                                  <ElapsedTimeChip>
                                    {elapsedTime}
                                  </ElapsedTimeChip>
                                )}
                            </Stack>
                          </Collapse>
                          {inCommingMessage && (
                            <Markdown>{inCommingMessage}</Markdown>
                          )}
                        </CardContent>
                      </GlassMorphicCard>
                    </Stack>
                  </Fade>
                )}

                {/* Error Display */}
                {errorMessage && (
                  <Fade in timeout={300}>
                    <Alert
                      severity="error"
                      onClose={() => setErrorMessage(null)}
                      sx={{
                        borderRadius: "16px",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      {errorMessage}
                    </Alert>
                  </Fade>
                )}
              </Stack>
            </Scrollbar>
          )}

          {/* Input Area */}
          <Stack gap={2}>
            <ModernInputCard>
              <CardContent sx={{ p: 3 }}>
                <form onSubmit={handleSendMessage}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={6}
                    placeholder="Ask me anything..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={loading}
                    variant="standard"
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        fontSize: "16px",
                        lineHeight: 1.5,
                      },
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          sx={{ alignSelf: "flex-end", mb: 0.5 }}
                        >
                          {loading ? (
                            <StyledIconButton
                              variant="stop"
                              onClick={stopGeneration}
                            >
                              <StopRoundedIcon />
                            </StyledIconButton>
                          ) : (
                            <StyledIconButton
                              variant="send"
                              type="submit"
                              disabled={!message.trim()}
                            >
                              <SendRoundedIcon />
                            </StyledIconButton>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </form>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={2}
                  gap={2}
                >
                  <Stack direction="row" spacing={1}>
                    <DeepAnalysisToggle
                      enabled={deepAnalysis}
                      onClick={handleDeepAnalysisToggle}
                      title={
                        deepAnalysis
                          ? "Click to disable Deep Analysis mode"
                          : "Enable Deep Analysis for enhanced reasoning"
                      }
                    >
                      <InsightsRoundedIcon
                        sx={{
                          fontSize: 18,
                          color: deepAnalysis
                            ? "primary.main"
                            : "text.secondary",
                          transition: "color 0.2s",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: deepAnalysis
                            ? "primary.main"
                            : "text.secondary",
                          transition: "all 0.2s",
                        }}
                      >
                        Deep Analysis
                      </Typography>
                    </DeepAnalysisToggle>

                    <DeepAnalysisToggle
                      enabled={deepDive}
                      onClick={handleDeepDiveToggle}
                      title={
                        deepDive
                          ? "Click to disable Deep Dive mode"
                          : "Enable Deep Dive for comprehensive analysis"
                      }
                    >
                      <BatchPredictionRoundedIcon
                        sx={{
                          fontSize: 18,
                          color: deepDive ? "primary.main" : "text.secondary",
                          transition: "color 0.2s",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: deepDive ? "primary.main" : "text.secondary",
                          transition: "all 0.2s",
                        }}
                      >
                        Deep Dive
                      </Typography>
                    </DeepAnalysisToggle>
                  </Stack>
                </Stack>
              </CardContent>
            </ModernInputCard>

            <Box
              sx={{
                my: 0.5,
                mx: "auto",
                color: theme.palette.grey[500],
                fontSize: "12px",
              }}
            >
              Mistakes happen. Review SAANVI's output
            </Box>
          </Stack>
        </Container>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "success" ? 3000 : 4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: "12px",
            backdropFilter: "blur(10px)",
          }}
          icon={
            snackbar.isServerError ? (
              <ErrorOutlineIcon />
            ) : snackbar.severity === "warning" ? (
              <WifiOffIcon />
            ) : undefined
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
